import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import { Form } from "@/components/ui/form";
import { Diff } from "@/features/FormCard/Diff";
import useCurrencyOptions from "@/hooks/useCurrencyOption";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
} from "@heroui/react";

import {
  AutocompleteAsyncField,
  AutocompleteField,
  AutocompleteLabelField,
  InputField,
  SelectFieldWithSection,
} from "@/features/FormCard/Fields";

import { useDynamicForm } from "@/features/Edit/dynamic-edit-form";
import { FormActionsUpdate } from "@/features/FormCard/Actions";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";
import { DynamicFields } from "@/features/FormCard/DynamicCard";
import { useMemo } from "react";

import { ExternalLink } from "@/components/ExternalLink";
import { Label } from "@radix-ui/react-label";
import {
  adjustDataForSubmission,
  countries_options,
  preprocessCountryData,
} from "@/components/Form/CountryPhone/data";
import { dynamicFormConfig, VALUATION_METHOD_OPTIONS } from "./edit-form-conf";
import { TechnicalInfoCardStatic } from "./technical_info_card";
import { getFieldTranslations } from "./translations";
import { SHOCK_TYPE_OPTIONS } from "./shock_type_options";
import { useTranslation } from "react-i18next";

export function M2StaticDatumsEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();
  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    isDeleting,
    changedFields,
    optionsMap,
    handleDelete,
    selectFieldsConfig,
    isLoadingTranslations,
    options,
  } = useDynamicForm({
    id,
    client,
    table_name,
    formConfig: dynamicFormConfig,
    adjustData: adjustDataForSubmission,
    adjustOnReset: (key, value, fetchedData) => {
      if (key === "m2_issuer_name") {
        return {
          text: fetchedData.m2_issuer_name,
          id: fetchedData.issuer_id,
        };
      }
      // VALUATION_METHOD_OPTIONS uses string values ("1".."6") but the column
      // is stored/returned as a number; AutocompleteLabelField requires a
      // string inputValue or HeroUI's Autocomplete throws on filtering.
      if (key === "valuation_method" && value !== null && value !== undefined) {
        return String(value);
      }
      return preprocessCountryData(key, value);
    },
  });

  const currencyOptions = useCurrencyOptions();

  const name = form.watch("name");
  const assetClass1 = form.watch("asset_class_1");
  const assetClass2 = form.watch("asset_class_2");
  const fieldTranslations = getFieldTranslations(t);

  const translatedDynamicConfig = useMemo(() => {
    const cfg = selectFieldsConfig?.asset_class_2;
    if (!cfg) return undefined;

    const translatedSections = Object.fromEntries(
      Object.entries(cfg.section).map(([sectionName, fields]) => [
        sectionName,
        Object.fromEntries(
          Object.entries(fields).map(([fieldName, fieldConfig]) => [
            fieldName,
            {
              ...fieldConfig,
              label: t(`m2_static_data.columns.${fieldName}`, {
                ns: "tables",
                defaultValue: fieldConfig.label,
              }),
            },
          ]),
        ),
      ]),
    );

    return {
      ...cfg,
      section: translatedSections,
    };
  }, [selectFieldsConfig?.asset_class_2, t]);

  return (
    <>
      <div className="grid place-items-center bg-gray-50 h-full w-full max-w-card">
        <div className="flex justify-center w-full">
          <Card className="p-4 bg-white w-full ">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <CardHeader className="font-bold text-gray-700">
                  {t("m2_static_data.title", {
                    ns: "tables",
                    defaultValue: "Actif",
                  })}{" "}
                  {id}
                </CardHeader>
                <Divider />
                <CardBody className="flex flex-col gap-4">
                  <Label>
                    {t("m2_static_data.sections.general_information", {
                      ns: "tables",
                      defaultValue: "Informations générales",
                    })}
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    <div className="md:col-span-8">
                      <InputField
                        name="name"
                        label={t("m2_static_data.columns.name", {
                          ns: "tables",
                          defaultValue: "Nom",
                        })}
                        isRequired={true}
                        capitalize={true}
                        control={form.control}
                      />
                    </div>
                    <div className="md:col-span-4">
                      <AutocompleteLabelField
                        name="currency"
                        label={t("m2_static_data.columns.currency", {
                          ns: "tables",
                          defaultValue: "Devise",
                        })}
                        options={currencyOptions}
                        isRequired={true}
                        control={form.control}
                        className="min-w-0"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                    <InputField
                      name="isin"
                      label={t("m2_static_data.columns.isin", {
                        ns: "tables",
                        defaultValue: "ISIN",
                      })}
                      control={form.control}
                    />
                    <InputField
                      name="code"
                      label={t("m2_static_data.columns.code", {
                        ns: "tables",
                        defaultValue: "Code",
                      })}
                      control={form.control}
                    />
                    <InputField
                      name="ticker"
                      label={t("m2_static_data.columns.ticker", {
                        ns: "tables",
                        defaultValue: "Ticker",
                      })}
                      control={form.control}
                    />
                  </div>
                  <AutocompleteAsyncField
                    name="m2_issuer_name"
                    label={t("m2_static_data.columns.m2_issuer_name", {
                      ns: "tables",
                      defaultValue: "Émetteur",
                    })}
                    control={form.control}
                    base_url="m2/m2_issuers"
                    id_column="issuer_id"
                    column_name="name"
                  />
                  <SelectFieldWithSection
                    listboxWrapperClassNames="!h-[400px] !max-h-none"
                    primary_key="asset_class_1"
                    name="asset_class_2"
                    label={t("m2_static_data.columns.asset_class_2", {
                      ns: "tables",
                      defaultValue: "Classe d'actifs",
                    })}
                    control={form.control}
                    sections={optionsMap.asset_class_2}
                    placeholder={t("form.placeholders.select")}
                    isRequired
                  />

                  <Divider />
                  <Label>
                    {t("m2_static_data.sections.custodian_information", {
                      ns: "tables",
                      defaultValue: "Informations dépositaire et réglementaires",
                    })}
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField
                      name="custodian"
                      label={t("m2_static_data.columns.custodian", {
                        ns: "tables",
                        defaultValue: "Dépositaire",
                      })}
                      control={form.control}
                    />
                    <InputField
                      name="custodian_code"
                      label={t("m2_static_data.columns.custodian_code", {
                        ns: "tables",
                        defaultValue: "Code dépositaire",
                      })}
                      control={form.control}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <AutocompleteField
                      name="custodian_code_type"
                      label={t("m2_static_data.columns.custodian_code_type", {
                        ns: "tables",
                        defaultValue: "Type de code dépositaire",
                      })}
                      options={options?.["custodian_code_type"] ?? []}
                      control={form.control}
                    />
                    <AutocompleteLabelField
                      name="custodian_country_code"
                      label={t("m2_static_data.columns.custodian_country_code", {
                        ns: "tables",
                        defaultValue: "Pays du dépositaire",
                      })}
                      options={countries_options}
                      control={form.control}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <AutocompleteField
                      name="climate_risk"
                      label={t("m2_static_data.columns.climate_risk", {
                        ns: "tables",
                        defaultValue: "Risque climatique",
                      })}
                      options={options?.["climate_risk"] ?? []}
                      control={form.control}
                    />
                    <AutocompleteLabelField
                      name="valuation_method"
                      label={t("m2_static_data.columns.valuation_method", {
                        ns: "tables",
                        defaultValue: "Méthode de valorisation",
                      })}
                      options={VALUATION_METHOD_OPTIONS}
                      control={form.control}
                    />
                  </div>
                  <AutocompleteLabelField
                    name="shock_type"
                    label={t("m2_static_data.columns.shock_type", {
                      ns: "tables",
                      defaultValue: "Type de choc",
                    })}
                    options={SHOCK_TYPE_OPTIONS}
                    control={form.control}
                  />
                  <DynamicFields
                    primaryValue={assetClass1}
                    fieldValue={assetClass2}
                    control={form.control}
                    config={translatedDynamicConfig}
                    isLoadingTranslations={isLoadingTranslations}
                    options={options}
                  />
                </CardBody>
                <CardFooter>
                  <FormActionsUpdate
                    onReset={handleReset}
                    onDelete={openDeleteModal}
                    isDirty={form.formState.isDirty}
                    elementId={id}
                    client={client}
                    allowDelete={false}
                  />
                </CardFooter>
              </form>
            </Form>
          </Card>
          {name && (
            <div className="absolute top-[3.20rem] right-12 2xl:right-10 lg:flex gap-2 flex-row hidden">
              {form.watch("m2_issuer_name")?.id && (
                <Button
                  href={toWorkspacePath(
                    `/admin/m2/m2_issuers/${form.watch("m2_issuer_name")?.id}`,
                  )}
                  variant="bordered"
                  color="primary"
                  showAnchorIcon
                  as={ExternalLink}
                  radius="full"
                  className="bg-white border-1 border-neutral-300 shadow-xl"
                >
                  {t("m2_static_data.columns.m2_issuer_name", {
                    ns: "tables",
                    defaultValue: "Émetteur",
                  })}
                </Button>
              )}
            </div>
          )}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
            children={
              changedFields
                ? Diff(
                    changedFields,
                    ["issuer_id"],
                    fieldTranslations,
                    table_name,
                  )
                : t("no_changes")
            }
          />

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </div>
      </div>
      <TechnicalInfoCardStatic
        id={Number(id)}
        table_name={table_name}
        strategy_id_control={form.control}
        client={client}
      />
    </>
  );
}
